import { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import PanelWithHelp from '../../PanelWithHelp';
import { useAuthContext, updateGameData } from '../../../contexts/AuthContext';
import { startSimulation } from '../../../services/Student';
import StatusPanel from './StatusPanel';
import WeatherForecastPanel from './WeatherForecastPanel';
import OutputPanel from './OutputPanel';
import GameParameters from './GameParameters';
import ScorePanel from './ScorePanel';
import useDimensions from '../../../helpers/useDimensions';
import Popup from '../../Popup';
import { getHelpUrl } from '../../../services/shared';

const MainContainer = styled.div`
    display: flex;
    gap: 16px;
    align-items: flex-start;
`;

export const GameParametersPanel = styled(Paper)``;

const OutputPanelsContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    /* max-height: 850px; */
    gap: 8px;
    min-width: 0;
`;

const SubPanel = styled(PanelWithHelp)`
    padding: ${({ $wrapper }) => ($wrapper ? 0 : 8)}px;
    height: 100%;
`;

const ChartPanel = styled(SubPanel)`
    height: 216px;
    min-width: 0px;
`;
const ChartPanelPopup = styled(SubPanel)`
    width: 90vw;
    height: 90vh;
`;

const ProgressImageContainer = styled.div`
    overflow: hidden;
    max-width: 300px;
    margin-left: auto;
    margin-right: auto;
    display: flex;
    align-items: center;
    height: 100%;
    /* background: #666; */

    &:hover {
        cursor: zoom-in;
    }
`;
const ProgressImage = styled.img`
    width: 100%;
    vertical-align: bottom;
`;

const Game = ({ show = true }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { auth, dispatch } = useAuthContext();
    const {
        game,
        gameConfig,
        gameStatus,
        token,
        fetching,
        gameForecast,
        gameSession,
    } = auth;
    const [formValues, setFormValues] = useState({});
    const [busy, setBusy] = useState(false);
    const [previousTry, setPreviousTry] = useState();
    const outputPanelsContainerRef = useRef(null);
    const [outputPanelsWidth] = useDimensions(outputPanelsContainerRef);

    const [chartPopupOpen, setChartPopupOpen] = useState(false);

    const [plantImagePopup, setPlantImagePopup] = useState(false);
    const { plant: plantImage } = gameStatus;
    const imgBaseUrl = `${process.env.PUBLIC_URL}/images/plant/`;
    const progressImageSrc = plantImage
        ? `${imgBaseUrl}${plantImage.replace(',', '.')}`
        : null;

    useEffect(() => {
        if (game) {
            const gameParameters = Object.entries(game).filter(
                ([key, value]) => {
                    const paramConfig = gameConfig[key];

                    return paramConfig?.type === 'game';
                }
            );
            const initialValues = gameParameters.reduce((prev, cur) => {
                const [key, value] = cur;
                return { ...prev, [key]: value };
            }, {});
            setFormValues(initialValues);
        }
    }, [game, gameConfig]);

    const handleInputChange = (inputKey) => (value) => {
        setFormValues((prevValues) => {
            return {
                ...prevValues,
                [inputKey]: value,
            };
        });
    };

    const handleSimulation = (def = false) => {
        let mounted = true;
        const oldData = gameSession?.histdata;
        setBusy(true);
        const mergedGameData = Object.assign({}, game, formValues);
        startSimulation(token, mergedGameData, def)
            .then((response) => {
                setPreviousTry(def ? null : oldData);
                if (response) dispatch(updateGameData(response));
                // reload();
            })
            .catch((err) => {
                enqueueSnackbar(
                    <div>
                        <p>{t('error.runSimulation')}</p>
                        <p>{err.message}</p>
                    </div>,
                    {
                        variant: 'error',
                    }
                );
            })
            .finally(() => {
                if (mounted) {
                    setBusy(false);
                }
            });

        return () => {
            mounted = false;
        };
    };
    const handleTryOutSettings = () => {
        handleSimulation(false);
    };
    const handleFinal = () => {
        handleSimulation(true);
    };

    if (!game || !formValues || !show) return null;
    const { language } = game;

    const smallScreen = outputPanelsWidth < 600;
    const help = gameConfig?.['@help']?.user;
    const getFullHelpUrl = (paramUrl) =>
        getHelpUrl(paramUrl, gameConfig, auth.languageId);

    return (
        <MainContainer>
            <PanelWithHelp helpUrl={getFullHelpUrl(help?.gameparams)}>
                <GameParameters
                    {...{
                        formValues,
                        handleInputChange,
                        handleTryOutSettings,
                        handleFinal,
                        language,
                        gameConfig,
                        game,
                        busy,
                        fetching,
                        gameStatus,
                    }}
                />
            </PanelWithHelp>

            <OutputPanelsContainer ref={outputPanelsContainerRef}>
                {smallScreen ? (
                    <>
                        <Grid
                            container
                            spacing={1}
                            direction="row"
                            wrap="nowrap"
                        >
                            <Grid
                                item
                                xs
                                container
                                direction="column"
                                spacing={1}
                            >
                                <Grid item xs>
                                    <SubPanel
                                        helpUrl={getFullHelpUrl(help?.progress)}
                                    >
                                        <StatusPanel
                                            status={auth.gameStatus}
                                            style={{ fontSize: '12px' }}
                                        />
                                    </SubPanel>
                                </Grid>

                                <Grid item xs>
                                    <SubPanel
                                        $wrapper
                                        helpUrl={getFullHelpUrl(help?.score)}
                                    >
                                        <ScorePanel
                                            status={auth.gameStatus}
                                            smallScreen={plantImage}
                                        />
                                    </SubPanel>
                                </Grid>
                            </Grid>

                            {plantImage && (
                                <Grid
                                    item
                                    xs={6}
                                    onClick={() => {
                                        setPlantImagePopup(true);
                                    }}
                                >
                                    <SubPanel
                                        $wrapper
                                        helpUrl={getFullHelpUrl(help?.plant)}
                                    >
                                        <ProgressImageContainer>
                                            <ProgressImage
                                                src={progressImageSrc}
                                                alt="Plant growth progress"
                                            />
                                        </ProgressImageContainer>
                                    </SubPanel>
                                </Grid>
                            )}
                        </Grid>

                        <SubPanel helpUrl={getFullHelpUrl(help?.forecast)}>
                            <WeatherForecastPanel forecast={gameForecast} />
                        </SubPanel>
                    </>
                ) : (
                    <Grid container spacing={0} style={{ gap: 8 }}>
                        <Grid
                            item
                            xs={9}
                            container
                            spacing={1}
                            direction="column"
                            wrap="nowrap"
                        >
                            <Grid item container spacing={1}>
                                <Grid item xs={6}>
                                    <SubPanel
                                        helpUrl={getFullHelpUrl(help?.progress)}
                                    >
                                        <StatusPanel status={auth.gameStatus} />
                                    </SubPanel>
                                </Grid>
                                <Grid item xs={6}>
                                    <SubPanel
                                        $wrapper
                                        helpUrl={getFullHelpUrl(help?.score)}
                                    >
                                        <ScorePanel status={auth.gameStatus} />
                                    </SubPanel>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <SubPanel
                                    helpUrl={getFullHelpUrl(help?.forecast)}
                                >
                                    <WeatherForecastPanel
                                        forecast={gameForecast}
                                    />
                                </SubPanel>
                            </Grid>
                        </Grid>
                        {plantImage && (
                            <Grid
                                item
                                xs={3}
                                onClick={() => {
                                    setPlantImagePopup(true);
                                }}
                            >
                                <SubPanel
                                    $wrapper
                                    helpUrl={getFullHelpUrl(help?.plant)}
                                >
                                    <ProgressImageContainer>
                                        <ProgressImage
                                            src={progressImageSrc}
                                            alt="Plant growth progress"
                                        />
                                    </ProgressImageContainer>
                                </SubPanel>
                            </Grid>
                        )}
                    </Grid>
                )}
                <ChartPanel helpUrl={getFullHelpUrl(help?.histoutput)}>
                    <OutputPanel
                        title={t('charts.histoutput.title')}
                        chartHeight="100%"
                        data={gameSession?.histdata}
                        previousData={previousTry}
                        game={game}
                        gameConfig={gameConfig}
                        outputType="histoutput"
                        component={ChartPanel}
                        gameStatus={gameStatus}
                        openPopup={() => setChartPopupOpen('histoutput')}
                    />
                </ChartPanel>
                <ChartPanel helpUrl={getFullHelpUrl(help?.lastoutput)}>
                    <OutputPanel
                        title={t('charts.lastoutput.title')}
                        chartHeight="100%"
                        data={gameSession?.lastdata}
                        game={game}
                        gameConfig={gameConfig}
                        outputType="lastoutput"
                        component={ChartPanel}
                        gameStatus={gameStatus}
                        openPopup={() => setChartPopupOpen('lastoutput')}
                    />
                </ChartPanel>
            </OutputPanelsContainer>
            <Popup
                open={plantImagePopup}
                onClose={() => {
                    setPlantImagePopup(false);
                }}
            >
                <img
                    src={progressImageSrc}
                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                    alt="Plant growth progress"
                />
            </Popup>
            <Popup
                open={chartPopupOpen}
                onClose={() => {
                    setChartPopupOpen(null);
                }}
            >
                <ChartPanelPopup
                    helpUrl={getFullHelpUrl(help?.[chartPopupOpen])}
                >
                    <OutputPanel
                        title={
                            chartPopupOpen
                                ? t(`charts.${chartPopupOpen}.title`)
                                : null
                        }
                        chartHeight="100%"
                        data={
                            chartPopupOpen === 'histoutput'
                                ? gameSession?.histdata
                                : gameSession?.lastdata
                        }
                        game={game}
                        gameConfig={gameConfig}
                        outputType={chartPopupOpen}
                        gameStatus={gameStatus}
                    />
                </ChartPanelPopup>
            </Popup>
        </MainContainer>
    );
};

export default Game;
